import { Component, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';

import { BehaviorSubject, Observable } from 'rxjs';
import { UntypedFormGroup } from '@angular/forms';
import { ECookieConsentStringMappingEnum } from './shared/enums/cookie-consent.enums';
import { CookieConsentOverlayService } from './cookie-consent-overlay.service';
import { FormBuilderService } from '../form-builder/shared/services/form-builder.service';
import { Field, FormFieldGroup } from '../form-builder/shared/interfaces/hubspot-form-data';
import { GenericModalService } from '../generic-modal/services/generic-modal.service';
import { ResponsiveState } from '@viablelogic/ngx-responsive';
import { ConfigurationService, PortalConfigurationService, SubscriptionCollector, TranslationService } from '@draeger-academy/common';
import { AppInfoService } from '@draeger-academy/common/lib/services/app-info/app-info.service';
import { TranslateService } from '@ngx-translate/core';
import { HubspotTrackingService } from '../hubspot';

@Component({
    selector: 'da-cookie-consent-overlay',
    templateUrl: './cookie-consent-overlay.component.html',
    styleUrls: ['./cookie-consent-overlay.component.scss'],
    standalone: false
})
export class CookieConsentOverlayComponent implements OnInit {
    readonly _site: string;
    public cookieConsentTranslations: any;
    public cookieConsentActive = false;
    private _modalId: string;
    private prom3: boolean;
    // private _unsubscribe: Subject<void> = new Subject();
    public formFields: any;
    public formId: string;
    private _$cookieConsentForm: BehaviorSubject<UntypedFormGroup> = new BehaviorSubject(null);
    public isCrawler = false;
    public imprintURL: string;
    private _$isMobile: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private _$state1: BehaviorSubject<boolean> = new BehaviorSubject(true);
    private _$state2: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private _$translations: BehaviorSubject<any> = new BehaviorSubject({});
    @ViewChild('overlay', { static: true }) _overlay;
    @Output() closeOverlay: EventEmitter<boolean> = new EventEmitter<boolean>();

    @SubscriptionCollector()
    private _subscriptions;
    public portalHasCookieConstentOverlay = false;
    public currentPortal: string;

    private _$portalId: BehaviorSubject<string> = new BehaviorSubject(null);
    public get portalId$(): Observable<string> {
        return this._$portalId.asObservable();
    }
    private _$portalSiteSettings: BehaviorSubject<string> = new BehaviorSubject(null);
    public get portalSiteSettings$(): Observable<string> {
        return this._$portalSiteSettings.asObservable();
    }

    constructor(
        private _genericModalService: GenericModalService,
        private _appInfoService: AppInfoService,
        private _cookieConsentOverlayService: CookieConsentOverlayService,
        private _responsiveState: ResponsiveState,
        private _formBuilderService: FormBuilderService,
        private _configurationService: ConfigurationService,
        private _translationService: TranslationService,
        private _translateService: TranslateService,
        private _hubspotTrackingService: HubspotTrackingService,
        private _portalConfigurationService: PortalConfigurationService
        
    ) {
        this._site = this._appInfoService.AppInfo.site;
        this.cookieConsentTranslations = this._translateService.stream('dacookieconsent');
    }

    public get state1$(): Observable<boolean> {
        return this._$state1.asObservable();
    }

    public get state2$(): Observable<boolean> {
        return this._$state2.asObservable();
    }

    public get isMobile$(): Observable<boolean> {
        return this._$isMobile.asObservable();
    }

    public get cookieConsentForm$(): Observable<UntypedFormGroup> {
        return this._$cookieConsentForm.asObservable();
    }

    ngOnInit(): void {
        this._resizeHandler();

        this._translateService.stream('dacookieconsent').subscribe((data) => {
            this._$translations.next(data);
            this.cookieConsentTranslations = data;
        });
        const _isPartnerPage = this._configurationService.isPartnerPage();
        if (!_isPartnerPage) {
            this._buildOverlay();
        }

        if (this._configurationService.isPartnerPage()) {
            this._configurationService.currentPortal$.subscribe((portalId) => {
                if (portalId) {
                    this._$portalId.next(portalId);
                    this.currentPortal = this._$portalId.getValue();
                    this._configurationService.getConfigPartnerByPortal().then((values) => {
                        if (values) {
                            const tempFeatures = values.filter((setting) => setting.key.toLowerCase() === 'features');
                            let hasCheckPrerequest;
                            if (tempFeatures && tempFeatures[0] && tempFeatures[0].value && tempFeatures[0].value.length > 0) {
                                hasCheckPrerequest = tempFeatures[0].value.filter((setting) => setting.name === 'ActiveApplications');
                            }
                            if (hasCheckPrerequest && hasCheckPrerequest[0] && hasCheckPrerequest[0].enabled) {
                                this.portalHasCookieConstentOverlay = true;
                                setTimeout(() => {
                                    this._buildOverlay();
                                }, 500);
                            } else {
                                this.portalHasCookieConstentOverlay = false;
                            }
                        }
                    });
                    this._portalConfigurationService.portalPreferences$.subscribe((preferences) => {
                        this.imprintURL = this._portalConfigurationService.getPortalImprintURL(preferences);
                    });
                }
            });
        }
    }

    private _buildOverlay() {
        if (this._configurationService.hasFeature('ActiveApplications') || this.portalHasCookieConstentOverlay) {
            this._cookieConsentOverlayService.checkSiteCookieConsent();
            const _overlaySub = this._cookieConsentOverlayService.showCookieConsentOverlay$.subscribe((value) => {
                if (value) {
                    this._overlayHandler();
                    this._cookieConsentOverlayService.showCookieConsentOverlay$.next(false);
                    this._buildForm();
                }
                // setTimeout(() => _overlaySub?.unsubscribe()); // do not unsubscribe because the overlay could be shown repeatedly
            });
        }

        this._cookieConsentOverlayService.showCookieConsentOverlayFromFooter$.subscribe((value) => {
            if (value) {
                this._overlayHandler();
                this._cookieConsentOverlayService.showCookieConsentOverlay$.next(false);
                this._buildForm();
            }
            // setTimeout(() => _overlaySub?.unsubscribe()); // do not unsubscribe because the overlay could be shown repeatedly
        });
    }

    // private _checkGTM(): void {
    //     const _containerId = this._cookieConsentOverlayService.getContainerIdOfGTM();
    //     if (!!_containerId) {
    //         this._cookieConsentOverlayService.checkToLoadGTMAfterPageRendered();
    //     }
    // }

    private _resizeHandler(): void {
        this._subscriptions.push(
            this._responsiveState.elemento$.subscribe((val) => {
                this._$isMobile.next(val === 'xs' || val === 'sm' || val === 'md');
            })
        );
    }

    private _buildForm(): void {
        this.formFields = [
            {
                fields: [
                    // {
                    //     name: 'TechnicalRequired',
                    //     label: null,
                    //     type: 'enummeration',
                    //     fieldType: 'booleancheckbox',
                    //     defaultValue: true,
                    //     required: false,
                    //     dependentFieldFilters: [],
                    //     enabled: false,
                    //     hidden: true
                    // },
                    {
                        name: 'Functional',
                        label: this.cookieConsentTranslations.Functional,
                        description: this.cookieConsentTranslations.FunctionalDescription,
                        type: 'enummeration',
                        fieldType: 'booleancheckbox',
                        defaultValue: this._cookieConsentOverlayService.$cookieConsentData.getValue().Functional === 1 ? true : false,
                        required: false,
                        dependentFieldFilters: [],
                        enabled: true
                    },
                    {
                        name: 'Statistics',
                        label: this.cookieConsentTranslations.Statistics,
                        description: this.cookieConsentTranslations.StatisticsDescription,
                        type: 'enummeration',
                        fieldType: 'booleancheckbox',
                        defaultValue: this._cookieConsentOverlayService.$cookieConsentData.getValue().Statistics === 1 ? true : false,
                        required: false,
                        dependentFieldFilters: [],
                        enabled: true
                    },
                    {
                        name: 'Marketing',
                        label: this.cookieConsentTranslations.Marketing,
                        description: this.cookieConsentTranslations.MarketingDescription,
                        type: 'enummeration',
                        fieldType: 'booleancheckbox',
                        defaultValue: this._cookieConsentOverlayService.$cookieConsentData.getValue().Marketing === 1 ? true : false,
                        required: false,
                        dependentFieldFilters: [],
                        enabled: true
                    }
                ]
            }
        ];
        const _controls = this._formBuilderService.getReactiveFormFromSimpleSchema(this._getFlattendData(this.formFields));
        this._$cookieConsentForm.next(_controls);
    }

    private _getFlattendData(data: any) {
        const _flattened: Array<any> = [];
        data.forEach((formFieldGroup) => {
            formFieldGroup.fields.forEach((field: Field) => {
                _flattened.push(field);
            });
        });
        return _flattened;
    }

    private _overlayHandler(): void {
        this._$state1.next(true);
        this._$state2.next(false);
        this._modalId = this._genericModalService.create(this._overlay);
        this._genericModalService.open(this._modalId, null, false, 'da-modal cookie-consent', true);
    }

    public acceptAll(): void {
        const _all = 'v=' + this.cookieConsentTranslations.Version + '&t=1&f=1&s=1&m=1';
        this._cookieConsentOverlayService.setCookie(_all);
        this._closeOverlay();
    }

    public rejectAll(): void {
        const _all = 'v=' + this.cookieConsentTranslations.Version + '&t=1&f=0&s=0&m=0';
        this._cookieConsentOverlayService.setCookie(_all);
        this._closeOverlay();
    }

    public showCookieSettings(): void {
        this._$state1.next(false);
        this._$state2.next(true);
        //this._triggerScrollState2();
    }

    public saveCookieSettings(): void {
        let _cookieData = 'v=' + this.cookieConsentTranslations.Version + '&t=1';
        this.formFields.forEach((formFieldGroup: FormFieldGroup) => {
            formFieldGroup.fields.forEach((field: Field) => {
                const _value = this._$cookieConsentForm.getValue().get(field.name).value ? 1 : 0;
                _cookieData += ECookieConsentStringMappingEnum[field.name] + _value;
            });
        });
        this._cookieConsentOverlayService.setCookie(_cookieData);
        this._closeOverlay();
    }

    private _closeOverlay(): void {
        this._genericModalService.close(this._modalId);
        this._cookieConsentOverlayService.showCookieConsentOverlay$.next(false);
        if (this._cookieConsentOverlayService.showCookieConsentOverlayFromFooter$.getValue() === true) {
            this.closeOverlay.emit(false);
        } else {
            this.closeOverlay.emit(true);
        }
    }
}
